import { useLayoutEffect, useRef } from 'react';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { useSvgMarkup } from '@/features/overviewDiagram/hooks/useSvgMarkup';
import { applyHmiTheme } from '@/features/overviewDiagram/ui/hmiTheme';
import { typography, radius } from '@/shared/theme/tokens';

/**
 * Static (non-interactive) thumbnail of a template's SVG. Fetches + sanitizes via
 * the shared useSvgMarkup, inlines it, and applies the dark/light HMI theme.
 *
 * @param {object} props.template  SvgTemplate ({ id, name, svgUrl, sections })
 * @param {object} [props.rawMarkup]  optional already-sanitized markup (for upload preview)
 */
export function SvgThumbnail({ template, rawMarkup, height = 150 }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const hostRef = useRef(null);
  const { data: fetched, isLoading, isError } = useSvgMarkup(rawMarkup ? null : template);
  const markup = rawMarkup ?? fetched;

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || !markup) return;
    host.innerHTML = markup;
    const svg = host.querySelector('svg');
    if (svg) {
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.style.display = 'block';
      svg.style.maxHeight = '100%';
      svg.style.pointerEvents = 'none';
      applyHmiTheme(svg, isDark);
    }
  }, [markup, isDark]);

  return (
    <div style={{
      height, width: '100%', borderRadius: radius.lg, overflow: 'hidden',
      background: isDark ? 'rgba(255,255,255,0.02)' : c.bgSubtle,
      border: `1px solid ${c.borderSubtle}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {isLoading && <span style={{ fontSize: typography.fontSize.base, color: c.textSubtle }}>Loading…</span>}
      {isError && !rawMarkup && (
        <span className="material-symbols-outlined" style={{ fontSize: 32, color: c.textMuted }}>broken_image</span>
      )}
      {markup && <div ref={hostRef} style={{ width: '100%', height: '100%', padding: 8, boxSizing: 'border-box' }} />}
    </div>
  );
}
